import { useParams, Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, BookOpen, Bookmark, ChevronDown, ChevronUp,
  Copy, Check, ScrollText, Layers, AlertCircle, RefreshCw,
} from "lucide-react";
import { Section, Badge, AnimatedCard, OrnamentDivider } from "@/components/ui/shared";
import { useBookmarks } from "@/lib/bookmarks";
import { useToast } from "@/components/ui/Toast";
import ReadingProgress from "@/components/ui/ReadingProgress";
import Breadcrumbs from "@/components/ui/Breadcrumbs";
import { useSEO } from "@/hooks/useSEO";
import { snippet } from "@/lib/seo";
import { SkeletonDetailPage } from "@/components/ui/Skeleton";
import { errorMessage, isNotFound, useAyah, useSurah, usePageView } from "@/lib/api";
import type { AyahSummary, TafsirResponse } from "@/lib/api";

const TAFSIR_LABELS: Record<string, string> = {
  ibn_kathir: "Ibn Kathir",
  al_tabari: "al-Tabari",
  al_qurtubi: "al-Qurtubi",
  al_saadi: "as-Sa'di",
  al_jalalayn: "al-Jalalayn",
  fi_zilal: "Fi Zilal al-Quran",
};

/* ============================================
   Ayah Card Component
   ============================================ */

function AyahCard({
  ayah,
  surahName,
  index,
}: {
  ayah: AyahSummary;
  surahName: string;
  index: number;
}) {
  const [expanded, setExpanded] = useState(false);
  const [activeTafsir, setActiveTafsir] = useState(0);
  const [copied, setCopied] = useState(false);
  const { isBookmarked, toggle } = useBookmarks();
  const toast = useToast();

  // Tafsir comes from the ayah endpoint; fetch it only once the reader asks for it.
  const detail = useAyah(expanded ? ayah.surahNumber : undefined, expanded ? ayah.number : undefined);
  const tafsirs: TafsirResponse[] = detail.data?.tafsirs ?? [];
  const reference = `${ayah.surahNumber}:${ayah.number}`;
  const bookmarkId = `quran-${ayah.surahNumber}-${ayah.number}`;
  const bookmarked = isBookmarked(bookmarkId);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(`${ayah.textArabic}\n\n${ayah.textEnglish}\n\n— Quran ${reference}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy to the clipboard");
    }
  };

  const handleBookmark = () => {
    const added = toggle({
      id: bookmarkId,
      type: "quran",
      title: `Quran ${reference}`,
      subtitle: `Surah ${surahName}`,
      route: `/quran/${ayah.surahNumber}#ayah-${ayah.number}`,
    });
    toast.success(added ? "Bookmarked!" : "Bookmark removed");
  };

  return (
    <AnimatedCard delay={Math.min(index, 10) * 0.05}>
      <div id={`ayah-${ayah.number}`} className="scroll-mt-24 rounded-xl border border-[var(--color-cream-dark)] bg-white hover:border-purple-200 transition-all overflow-hidden">
        <div className="p-5 md:p-6">
          {/* Ayah Number */}
          <div className="flex items-start justify-between mb-4">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-white text-sm font-bold shadow-md" aria-label={`Ayah ${ayah.number}`}>
              {ayah.number}
            </div>
            <div className="flex items-center gap-1.5">
              {ayah.sajdah && <Badge value="sajdah" />}
              <button onClick={handleCopy} className="p-1.5 rounded-lg text-[var(--color-ink-muted)] hover:text-purple-600 hover:bg-purple-50 transition-colors" title="Copy ayah" aria-label="Copy ayah">
                {copied ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
              </button>
              <button onClick={handleBookmark} aria-pressed={bookmarked}
                className={`p-1.5 rounded-lg transition-colors ${bookmarked ? "text-amber-500 bg-amber-50" : "text-[var(--color-ink-muted)] hover:text-amber-500 hover:bg-amber-50"}`}
                title={bookmarked ? "Remove bookmark" : "Bookmark ayah"} aria-label={bookmarked ? "Remove bookmark" : "Bookmark ayah"}>
                <Bookmark className={`w-4 h-4 ${bookmarked ? "fill-current" : ""}`} />
              </button>
            </div>
          </div>

          {/* Arabic Text */}
          <p lang="ar" dir="rtl" className="font-arabic text-2xl md:text-3xl text-[var(--color-midnight)] leading-[2.2] text-right mb-4">
            {ayah.textArabic}
          </p>

          {/* Translation */}
          <p className="text-sm md:text-base text-[var(--color-ink)] leading-relaxed mb-1">
            {ayah.textEnglish}
          </p>

          <button
            onClick={() => setExpanded(!expanded)}
            aria-expanded={expanded}
            className="mt-4 flex items-center gap-1.5 text-xs font-medium text-purple-600 hover:text-purple-700 transition-colors"
          >
            <ScrollText className="w-3.5 h-3.5" />
            {expanded ? "Hide tafsir" : "View tafsir"}
            {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
        </div>

        {/* Tafsir Section */}
        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="px-5 pb-5 md:px-6 md:pb-6 border-t border-[var(--color-cream-dark)]">
                {detail.isLoading ? (
                  <p className="text-xs text-[var(--color-ink-muted)] mt-4">Loading tafsir…</p>
                ) : detail.isError ? (
                  <p className="text-xs text-red-600 mt-4">{errorMessage(detail.error)}</p>
                ) : tafsirs.length === 0 ? (
                  <p className="text-xs text-[var(--color-ink-muted)] mt-4">No tafsir has been added for this ayah yet.</p>
                ) : (
                  <>
                    {/* Tafsir source tabs */}
                    <div className="flex flex-wrap gap-2 mt-4 mb-4" role="tablist">
                      {tafsirs.map((t, idx) => (
                        <button key={t.id} role="tab" aria-selected={activeTafsir === idx} onClick={() => setActiveTafsir(idx)}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                            activeTafsir === idx ? "bg-purple-100 text-purple-700" : "bg-[var(--color-cream)] text-[var(--color-ink-muted)] hover:bg-purple-50"
                          }`}>
                          {TAFSIR_LABELS[t.source] ?? t.source}
                        </button>
                      ))}
                    </div>

                    <div className="p-4 rounded-lg bg-purple-50/50 border border-purple-100" role="tabpanel">
                      <p className="text-xs font-semibold text-purple-700 mb-2">
                        Tafsir {TAFSIR_LABELS[tafsirs[activeTafsir]?.source] ?? tafsirs[activeTafsir]?.source}
                      </p>
                      <p className="text-sm text-[var(--color-ink)] leading-relaxed">
                        {tafsirs[activeTafsir]?.text}
                      </p>
                      {tafsirs[activeTafsir]?.textArabic && (
                        <p lang="ar" dir="rtl" className="font-arabic text-base text-[var(--color-gold-dim)]/70 mt-3 leading-loose text-right">
                          {tafsirs[activeTafsir].textArabic}
                        </p>
                      )}
                    </div>
                  </>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </AnimatedCard>
  );
}

/* ============================================
   Quran Surah Detail Page
   ============================================ */

export default function QuranDetailPage() {
  const { surahNum } = useParams<{ surahNum: string }>();
  const navigate = useNavigate();
  const { isBookmarked, toggle } = useBookmarks();
  const toast = useToast();

  // API hook
  const num = surahNum ? parseInt(surahNum, 10) : undefined;
  const { data: surah, isLoading, isError, error, refetch } = useSurah(num);

  const bookmarked = surahNum ? isBookmarked(`quran-${surahNum}`) : false;

  useSEO(
    surah ? `Surah ${surah.nameTransliteration} (${surah.nameEnglish}) — Tafsir & Translation` : "Surah",
    snippet(surah?.summary) ?? (surah ? `Read Surah ${surah.nameTransliteration}, ${surah.meaning}: all ${surah.ayahCount} ayahs in Arabic and English with tafsir.` : undefined),
    { type: "article", noindex: !isLoading && !surah },
  );

  usePageView("quran", { id: surah ? String(surah.number) : undefined, type: "surah", title: surah?.nameTransliteration });

  const handleBookmark = () => {
    if (!surah || !surahNum) return;
    const added = toggle({
      id: `quran-${surahNum}`,
      type: "quran",
      title: `Surah ${surah.nameTransliteration}`,
      subtitle: `${surah.meaning} · ${surah.ayahCount} ayahs`,
      route: `/quran/${surahNum}`,
    });
    toast.success(added ? "Bookmarked!" : "Bookmark removed");
  };

  // Loading
  if (isLoading) {
    return (
      <main className="pt-16">
        <div className="bg-gradient-to-b from-purple-950 to-[var(--color-midnight)] py-16 md:py-24" />
        <Section><SkeletonDetailPage /></Section>
      </main>
    );
  }

  // Error (a 404 falls through to "not found")
  if (isError && !isNotFound(error)) {
    return (
      <main className="pt-16">
        <Section>
          <div className="max-w-2xl mx-auto text-center py-20">
            <AlertCircle className="w-16 h-16 text-red-300 mx-auto mb-4" />
            <h2 className="font-[var(--font-display)] text-2xl font-bold text-[var(--color-midnight)] mb-2">Failed to Load Surah</h2>
            <p className="text-[var(--color-ink-muted)] mb-6">{errorMessage(error)}</p>
            <div className="flex items-center justify-center gap-3">
              <button onClick={() => void refetch()} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium hover:bg-[var(--color-cream)]">
                <RefreshCw className="w-3.5 h-3.5" /> Retry
              </button>
              <Link to="/quran" className="btn-gold inline-flex items-center gap-2 text-sm">
                <ArrowLeft className="w-4 h-4" /> Back to Quran
              </Link>
            </div>
          </div>
        </Section>
      </main>
    );
  }

  // Not found
  if (!surah) {
    return (
      <main className="pt-16">
        <Section>
          <div className="max-w-2xl mx-auto text-center py-20">
            <BookOpen className="w-16 h-16 text-[var(--color-cream-dark)] mx-auto mb-4" />
            <h2 className="font-[var(--font-display)] text-2xl font-bold text-[var(--color-midnight)] mb-2">Surah Not Found</h2>
            <p className="text-[var(--color-ink-muted)] mb-6">This surah doesn't exist or has been removed.</p>
            <Link to="/quran" className="btn-gold inline-flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" /> Back to Quran Companion
            </Link>
          </div>
        </Section>
      </main>
    );
  }

  const ayahs: AyahSummary[] = surah.ayahs ?? [];
  const themeList: string[] = surah.keyThemes ? surah.keyThemes.split(",").map(t => t.trim()).filter(Boolean) : [];

  return (
    <main className="pt-16">
      <ReadingProgress />
      {/* Hero */}
      <div className="bg-gradient-to-b from-purple-950 to-[var(--color-midnight)] py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 pattern-islamic opacity-20" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="flex items-center justify-between mb-6">
              <button onClick={() => navigate("/quran")} className="inline-flex items-center gap-2 text-[var(--color-cream)]/50 hover:text-[var(--color-cream)] text-sm transition-colors">
                <ArrowLeft className="w-4 h-4" /> Back to Quran Companion
              </button>
              <button onClick={handleBookmark}
                className={`p-2 rounded-lg transition-colors ${bookmarked ? "text-[var(--color-gold)] bg-[var(--color-gold)]/10" : "text-[var(--color-cream)]/40 hover:text-[var(--color-gold)] hover:bg-white/5"}`}>
                <Bookmark className={`w-5 h-5 ${bookmarked ? "fill-current" : ""}`} />
              </button>
            </div>

            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-white text-xl font-bold shadow-lg">
                {surah.number}
              </div>
            </div>

            <h1 className="font-[var(--font-display)] text-4xl md:text-5xl font-bold text-[var(--color-cream)]">
              {surah.nameTransliteration}
            </h1>
            <p lang="ar" dir="rtl" className="font-arabic text-3xl text-[var(--color-gold)]/70 mt-2">{surah.nameArabic}</p>
            <p className="text-[var(--color-cream)]/50 text-sm mt-2">
              {surah.meaning} · {surah.ayahCount} ayahs · <Badge value={surah.revelationType} className="!inline" />
            </p>
            {surah.summary && (
              <p className="text-[var(--color-cream)]/40 text-sm mt-4 max-w-xl mx-auto">{surah.summary}</p>
            )}

            {/* Themes */}
            {themeList.length > 0 && (
              <div className="flex flex-wrap justify-center gap-2 mt-6">
                {themeList.map((theme) => (
                  <span key={theme} className="px-3 py-1 rounded-full bg-white/10 text-[var(--color-cream)]/60 text-xs font-medium flex items-center gap-1.5">
                    <Layers className="w-3 h-3" /> {theme}
                  </span>
                ))}
              </div>
            )}
          </motion.div>
        </div>
      </div>

      {/* Ayahs */}
      <Section>
        <div className="max-w-3xl mx-auto">
          <Breadcrumbs crumbs={[{ label: "Quran", to: "/quran" }, { label: `Surah ${surah.nameTransliteration}` }]} />
          <div className="text-center mb-8">
            <p className="text-sm text-[var(--color-ink-muted)]">
              Tap "View tafsir" under an ayah to read commentary from classical scholars.
            </p>
          </div>

          <div className="space-y-4">
            {ayahs.map((ayah, i) => (
              <AyahCard key={ayah.id} ayah={ayah} surahName={surah.nameTransliteration} index={i} />
            ))}
          </div>

          <OrnamentDivider className="mt-12" />
          <div className="text-center mt-4">
            <p className="font-arabic text-lg text-[var(--color-gold)]/50">صَدَقَ اللَّهُ الْعَظِيمُ</p>
            <p className="text-xs text-[var(--color-ink-muted)] mt-1">Allah the Almighty has spoken the truth</p>
          </div>
        </div>
      </Section>
    </main>
  );
}
